import { useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from './auth-context';
import type { AuthContextType } from './auth-context-type';

// Permission definitions
const PERMISSIONS: Record<string, string[]> = {
  // Technical/diagnostic permissions (admin only)
  'hubspot:edge_functions': ['admin'],
  'hubspot:diagnostics': ['admin'],
  'hubspot:documentation': ['admin'],
  'hubspot:support': ['admin'],
  
  // Operational permissions (admin + manager)
  'hubspot:view': ['admin', 'manager'],
  'hubspot:analytics': ['admin', 'manager'],
  'hubspot:activity': ['admin', 'manager'],
  'hubspot:create_deals': ['admin', 'manager'],
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'manager' | 'editor' | 'user' | null>(null);

  const checkAdminRole = async (): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      const hasAdmin = !error && data !== null;
      setIsAdmin(hasAdmin);
      return hasAdmin;
    } catch (error) {
      console.error('Error checking admin role:', error);
      return false;
    }
  };

  const checkUserRole = async (): Promise<void> => {
    if (!user) {
      setUserRole(null);
      setIsAdmin(false);
      setIsManager(false);
      return;
    }
    
    try {
      // Get all roles for the user
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error checking user role:', error);
        setUserRole('user');
        setIsAdmin(false);
        setIsManager(false);
        return;
      }

      // If no roles found, default to 'user'
      if (!data || data.length === 0) {
        setUserRole('user');
        setIsAdmin(false);
        setIsManager(false);
        return;
      }

      // Prioritize roles: admin > manager > editor > user
      const roles = data.map(r => r.role as 'admin' | 'manager' | 'editor' | 'user');
      let highestRole: 'admin' | 'manager' | 'editor' | 'user' = 'user';
      
      if (roles.includes('admin')) {
        highestRole = 'admin';
      } else if (roles.includes('manager')) {
        highestRole = 'manager';
      } else if (roles.includes('editor')) {
        highestRole = 'editor';
      }

      setUserRole(highestRole);
      setIsAdmin(highestRole === 'admin');
      setIsManager(highestRole === 'manager' || highestRole === 'admin');
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole('user');
      setIsAdmin(false);
      setIsManager(false);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!userRole) return false;
    const allowedRoles = PERMISSIONS[permission] || [];
    return allowedRoles.includes(userRole);
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check user role when user logs in
        if (session?.user) {
          setTimeout(() => {
            checkUserRole();
          }, 0);
        } else {
          setIsAdmin(false);
          setIsManager(false);
          setUserRole(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          checkUserRole();
        }, 0);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setTimeout(() => {
        checkUserRole();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    setIsManager(false);
    setUserRole(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      isAdmin,
      isManager,
      userRole,
      hasPermission,
      signIn, 
      signOut,
      checkAdminRole,
      checkUserRole
    }}>
      {children}
    </AuthContext.Provider>
  );
}
